import {
  AddToCartErrorType,
  CreateBookingErrorType,
  FormError,
  FormErrorsType,
  GenericErrorType,
  PreviewErrorType,
} from '../../types/errors';
import {
  PlatformMetroServerError,
  PlatformServerError,
} from '../../../__tests__/mocks/ambassador/mockPlatformError';
import { ExperimentsConsts } from '../../consts/experiments';
import { mapToArray } from '../index';
import type { ResolvedError } from '@wix/error-handler';
import type { Experiments } from '@wix/yoshi-flow-editor';

export const getErrorByType = <ErrorType>({
  errorType,
  errors,
}: {
  errorType?: ErrorType;
  errors?: FormError[];
}): FormError =>
  errors?.find((error) =>
    mapToArray<FormErrorsType>(errorType as any).includes(error.errorType),
  )!;

const isErrorOfType = <ErrorType>(errorType: ErrorType, error: FormError) => {
  return mapToArray<FormErrorsType>(errorType as any).some(
    (expectedError) => error.errorType === expectedError,
  );
};

export const hasErrorOfType = <ErrorType>({
  errorType,
  errors,
}: {
  errorType: ErrorType;
  errors: FormError[];
}): boolean => {
  return errors.some((error) => isErrorOfType(errorType, error));
};

export function mapPreviewServerError(
  previewServerError: string,
): PreviewErrorType | GenericErrorType {
  if (previewServerError.includes(PreviewErrorType.INVALID_LINE_ITEM)) {
    return PreviewErrorType.INVALID_LINE_ITEM;
  } else if (
    previewServerError.includes(PreviewErrorType.FAILED_RESOLVING_SERVICE)
  ) {
    return PreviewErrorType.FAILED_RESOLVING_SERVICE;
  } else if (
    previewServerError.includes(
      PreviewErrorType.NUMBER_OF_PARTICIPANTS_NOT_FOUND,
    )
  ) {
    return PreviewErrorType.NUMBER_OF_PARTICIPANTS_NOT_FOUND;
  }
  return GenericErrorType.GENERIC_BOOK_ERROR;
}

export type BookingsServerError = {
  details: {
    applicationError: {
      code: CreateBookingErrorType;
      data?: {
        violations?: [{ description?: string }];
      };
    };
  };
};

export function mapPlatformError(
  error: PlatformServerError | PlatformMetroServerError,
) {
  return (
    (error as PlatformServerError)?.details ||
    (error as PlatformMetroServerError)?.data?.details
  );
}

const TEST_BOOKING_REJECTION_CODES = [
  'UNAUTHORIZED_OPERATION',
  'MIXED_DEMO_BOOKINGS',
];

export function mapCheckoutBookingError(
  error: PlatformServerError | PlatformMetroServerError | ResolvedError,
  experiments?: Experiments,
  isTestSession?: boolean,
) {
  const errorCode =
    (error as ResolvedError)?.applicationError?.code ??
    mapPlatformError(error as PlatformServerError | PlatformMetroServerError)
      ?.applicationError?.code;

  if (isTestSession && TEST_BOOKING_REJECTION_CODES.includes(errorCode)) {
    return GenericErrorType.TEST_BOOKING_NOT_AVAILABLE;
  }

  const shouldShowInvalidIntakeFormMessage = experiments?.enabled(
    ExperimentsConsts.ShowErrorMessageOnInvalidIntakeForm,
  );

  if (
    isErrorOfType(CreateBookingErrorType, {
      errorType: errorCode as FormErrorsType,
    }) ||
    (isErrorOfType(GenericErrorType, {
      errorType: errorCode as FormErrorsType,
    }) &&
      (errorCode !== GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION ||
        shouldShowInvalidIntakeFormMessage)) ||
    isErrorOfType(AddToCartErrorType, {
      errorType: errorCode as FormErrorsType,
    })
  ) {
    return errorCode;
  } else {
    return GenericErrorType.GENERIC_BOOK_ERROR;
  }
}
